import { useSTKContext } from '@/context/context';
import {
  BundleUpcharge,
  BundleUpchargeUpdatePayload,
  Patch,
  UpsertBundleUCPatchesRequest,
} from '@/types/bundles-upcharges.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useBundleUpcharges = () => {
  const { apiClient } = useSTKContext();
  return useQuery<BundleUpcharge[]>({
    queryKey: ['bundle-upcharges'],
    queryFn: async () => {
      const response = await apiClient.get<BundleUpcharge[]>(`/bundle-upcharges`);
      return response.data;
    },
  });
};

export const useUpdateBundleUpcharges = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BundleUpchargeUpdatePayload[]) => apiClient.put('/bundle-upcharges', payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['bundle-upcharges']);
    },
  });
};

export const findBundleUpchargeByType = (bundleUpcharges: BundleUpcharge[] | undefined, type: string) => {
  if (!bundleUpcharges) return null;
  return bundleUpcharges.find((upcharge) => upcharge.type === type);
};

export const patchesPayloadMapper = (bundleUpchargeSelected: BundleUpcharge) => {
  const { patches } = bundleUpchargeSelected;

  const patchesData = patches.map((patch) => {
    const patchData = {
      patchId: patch.patchId,
      name: patch.name,
      price: patch.price,
      isVisible: patch.isVisible,
    };
    return patchData;
  });
  return patchesData;
};

export const bundleUpchargesPayloadMapper = (bundleUpchargeSelected: BundleUpcharge, patchesPayload: Patch[]) => {
  const payload: BundleUpchargeUpdatePayload = {
    type: bundleUpchargeSelected.type,
    description: bundleUpchargeSelected?.description,
    patches: patchesPayload,
    isLock: bundleUpchargeSelected.isLocked,
  };

  return payload;
};

export const bundleUpchargesSort = (bundleUpcharges: BundleUpcharge[] | undefined) =>
  bundleUpcharges?.sort((a, b) => {
    if (a.description && !b.description) {
      return 1;
    }
    if (!a.description && b.description) {
      return -1;
    }
    return 0;
  });

export const isTheSameDescription = (upcharges: BundleUpcharge[] | undefined, value: string, typeSelected: string) => {
  if (!upcharges?.length || !value) return false;
  const valueFormatted = value?.split(' ').join('').toLowerCase();
  return (
    upcharges?.some(
      (upcharge) =>
        upcharge?.description?.split(' ').join('').toLowerCase() === valueFormatted && upcharge.type !== typeSelected,
    ) && Boolean(value)
  );
};
export const useUpsertBundleUpchargePatches = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: UpsertBundleUCPatchesRequest) => {
    const response = await apiClient.put('/bundle-upcharges/upsert-patches', requestBody);
    return response.data;
  });
};
